import type { SettingsNlsT } from './SettingsNls'
import type { NlsKey } from '../../nls/strings_nls'

export type SettingsSection =
  | 'PROFILE'
  | 'GENERAL'
  | 'SECURITY'
  | 'DEVICES'
  | 'SUPPORT'
  | 'BILLING'
  | 'APPEARANCE'
  | 'NOTIFICATIONS'
  | 'AI_MAGIC'
  | 'DESIGN_EDITOR'
  | 'APP_GUIDES'
  | 'INTERACTIVE_TOURS'
  | 'ACTIONS_CATALOG'
  | 'FEEDBACK_CENTER'

export type SettingsItemMetadata = {
  id: SettingsSection
  title: string
  detail: string
}

export type SettingsGroupMetadata = {
  id: string
  label: string
  items: SettingsItemMetadata[]
}

export const SETTINGS_ITEM_TOUR_ID: Record<SettingsSection, string> = {
  PROFILE: 'settings-profile',
  GENERAL: 'settings-general',
  SECURITY: 'settings-security',
  DEVICES: 'settings-devices',
  SUPPORT: 'settings-support',
  BILLING: 'settings-billing',
  APPEARANCE: 'settings-appearance',
  NOTIFICATIONS: 'settings-notifications',
  AI_MAGIC: 'settings-ai-magic',
  DESIGN_EDITOR: 'settings-design-editor',
  APP_GUIDES: 'settings-app-guides',
  INTERACTIVE_TOURS: 'settings-interactive-tours',
  ACTIONS_CATALOG: 'settings-actions-catalog',
  FEEDBACK_CENTER: 'settings-feedback-center',
}

export const SETTINGS_NAVIGATION_GROUPS: SettingsGroupMetadata[] = [
  {
    id: 'account',
    label: 'Account',
    items: [
      { id: 'PROFILE', title: 'Profile', detail: 'Identity, avatar, locale' },
      { id: 'GENERAL', title: 'Account & data', detail: 'Account, data export & data lifecycle' },
      { id: 'SECURITY', title: 'Security & devices', detail: 'Watchdog session lease & phone apps' },
      { id: 'BILLING', title: 'Billing', detail: 'Credits, tier, balances' },
    ],
  },
  {
    id: 'preferences',
    label: 'Preferences',
    items: [
      { id: 'APPEARANCE', title: 'Appearance', detail: 'Themes, soundscapes, animation profile' },
      { id: 'NOTIFICATIONS', title: 'Notifications', detail: 'Push, Guardian voice, achievements' },
      { id: 'AI_MAGIC', title: 'AI Magic', detail: 'Line, quick answers, draft alerts' },
      { id: 'DESIGN_EDITOR', title: 'Design Editor', detail: 'Canvas defaults, autosave, export' },
    ],
  },
  {
    id: 'help',
    label: 'Help',
    items: [
      { id: 'APP_GUIDES', title: 'App Guides', detail: 'Cinematic walkthroughs of core flows' },
      { id: 'INTERACTIVE_TOURS', title: 'Interactive Tours', detail: 'Guided spotlight walkthroughs' },
      { id: 'ACTIONS_CATALOG', title: 'Actions Catalog', detail: 'Directory of system actions and how-tos' },
      { id: 'FEEDBACK_CENTER', title: 'Feedback Center', detail: 'Unanswered & skipped feedback' },
      { id: 'SUPPORT', title: 'Support', detail: 'Tickets, diagnostics, resolution' },
    ],
  },
]

export const SETTINGS_SECTION_NLS_KEYS: Record<SettingsSection, { title: NlsKey; detail: NlsKey; panelTitle?: NlsKey; panelSubtitle: NlsKey }> = {
  PROFILE: { title: 'NLSSettingsSectionProfile', detail: 'NLSSettingsSectionProfileDetail', panelSubtitle: 'NLSSettingsSectionProfileSubtitle' },
  GENERAL: { title: 'NLSSettingsSectionGeneral', detail: 'NLSSettingsSectionGeneralDetail', panelSubtitle: 'NLSSettingsSectionGeneralSubtitle' },
  SECURITY: { title: 'NLSSettingsSectionSecurity', detail: 'NLSSettingsSectionSecurityDetail', panelSubtitle: 'NLSSettingsSectionSecuritySubtitle' },
  DEVICES: { title: 'NLSSettingsSectionDevices', detail: 'NLSSettingsSectionDevicesDetail', panelSubtitle: 'NLSSettingsSectionDevicesSubtitle' },
  SUPPORT: { title: 'NLSSettingsSectionSupport', detail: 'NLSSettingsSectionSupportDetail', panelSubtitle: 'NLSSettingsSectionSupportSubtitle' },
  BILLING: { title: 'NLSSettingsSectionBilling', detail: 'NLSSettingsSectionBillingDetail', panelSubtitle: 'NLSSettingsSectionBillingSubtitle' },
  APPEARANCE: { title: 'NLSSettingsSectionAppearance', detail: 'NLSSettingsSectionAppearanceDetail', panelTitle: 'NLSSettingsSectionAppearanceTitle', panelSubtitle: 'NLSSettingsSectionAppearanceSubtitle' },
  NOTIFICATIONS: { title: 'NLSSettingsSectionNotifications', detail: 'NLSSettingsSectionNotificationsDetail', panelSubtitle: 'NLSSettingsSectionNotificationsSubtitle' },
  AI_MAGIC: { title: 'NLSSettingsSectionAiMagic', detail: 'NLSSettingsSectionAiMagicDetail', panelSubtitle: 'NLSSettingsSectionAiMagicSubtitle' },
  DESIGN_EDITOR: { title: 'NLSSettingsSectionDesignEditor', detail: 'NLSSettingsSectionDesignEditorDetail', panelSubtitle: 'NLSSettingsSectionDesignEditorSubtitle' },
  APP_GUIDES: { title: 'NLSSettingsSectionAppGuides', detail: 'NLSSettingsSectionAppGuidesDetail', panelSubtitle: 'NLSSettingsSectionAppGuidesSubtitle' },
  INTERACTIVE_TOURS: { title: 'NLSSettingsSectionInteractiveTours', detail: 'NLSSettingsSectionInteractiveToursDetail', panelSubtitle: 'NLSSettingsSectionInteractiveToursSubtitle' },
  ACTIONS_CATALOG: { title: 'NLSSettingsSectionActionsCatalog', detail: 'NLSSettingsSectionActionsCatalogDetail', panelSubtitle: 'NLSSettingsSectionActionsCatalogSubtitle' },
  FEEDBACK_CENTER: { title: 'NLSSettingsSectionFeedbackCenter', detail: 'NLSSettingsSectionFeedbackCenterDetail', panelSubtitle: 'NLSSettingsSectionFeedbackCenterSubtitle' },
}

export const SETTINGS_PANEL_HEADERS: Record<SettingsSection, { title: string; subtitle: string }> = {
  PROFILE: { title: 'Profile', subtitle: 'Identity, avatar, display mode, and language.' },
  GENERAL: { title: 'Account & data', subtitle: 'Account details, data export, and end-of-life data controls.' },
  SECURITY: { title: 'Security & devices', subtitle: 'Watchdog lease timing, session cache handling, phone apps, and updates.' },
  DEVICES: { title: 'Devices', subtitle: 'Install the Timelining phone app and check for updates.' },
  SUPPORT: { title: 'Support', subtitle: 'Write support tickets, optionally attach diagnostics, and track owner resolution.' },
  BILLING: { title: 'Billing', subtitle: 'Credits ledger, profile balance, and your active tier.' },
  APPEARANCE: { title: 'Appearance & Engine', subtitle: 'Themes, soundscapes, and the celebration engine.' },
  NOTIFICATIONS: { title: 'Notifications', subtitle: 'Choose what notifies you and how.' },
  AI_MAGIC: { title: 'AI Magic', subtitle: 'Line behavior, quick answers, and draft notifications.' },
  DESIGN_EDITOR: { title: 'Design Editor', subtitle: 'Canvas defaults, autosave cadence, and export presets.' },
  APP_GUIDES: { title: 'App Guides', subtitle: 'Cinematic walkthroughs you can replay anytime.' },
  INTERACTIVE_TOURS: { title: 'Interactive Tours', subtitle: 'Guided in-app journeys that move with you.' },
  ACTIONS_CATALOG: { title: 'Actions Catalog', subtitle: 'Browse system actions, run eligible commands, or launch interactive how-tos.' },
  FEEDBACK_CENTER: { title: 'Feedback Center', subtitle: 'Inline forms for prompts you haven’t answered yet.' },
}

export const listSettingsSections = () =>
  SETTINGS_NAVIGATION_GROUPS.flatMap((group) => group.items.map((item) => item.id))

export const getSettingsPanelHeader = (section: SettingsSection, t?: SettingsNlsT) => {
  if (!t) return SETTINGS_PANEL_HEADERS[section]
  const keys = SETTINGS_SECTION_NLS_KEYS[section]
  return {
    title: t(keys.panelTitle ?? keys.title),
    subtitle: t(keys.panelSubtitle),
  }
}

export const getSettingsNavigationGroups = (t?: SettingsNlsT): SettingsGroupMetadata[] => {
  if (!t) return SETTINGS_NAVIGATION_GROUPS
  return SETTINGS_NAVIGATION_GROUPS.map((group) => ({
    ...group,
    label: group.id === 'account'
      ? t('NLSSettingsGroupAccount')
      : group.id === 'preferences'
        ? t('NLSSettingsGroupPreferences')
        : group.id === 'help'
          ? t('NLSSettingsGroupHelp')
          : group.label,
    items: group.items.map((item) => ({
      ...item,
      title: t(SETTINGS_SECTION_NLS_KEYS[item.id].title),
      detail: t(SETTINGS_SECTION_NLS_KEYS[item.id].detail),
    })),
  }))
}
